import {useState, useEffect, useRef} from 'react'
import { projectAPI } from '../../../api'
import {Spinner} from 'react-bootstrap'
import BootstrapDateRangePicker from '../../../components/BootstrapDateRangePicker'
import { Line } from 'react-chartjs-2'

interface ChartData {
  labels: string[];
  data: { [key: string]: number[] };
}

const RequestChat = ({logs, title, label}) => {

  const chartRef = useRef(null)
  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: { label: string; data: number[] }[];
  } | null>(null)

  useEffect(() => { 
    if (logs) {
      const datasets = Object.entries(logs.data).map(([label, data]) => ({
        label,
        data: data as number[],
        borderWidth:1,
        fill: true
      }));
      const data = {
        labels: logs.labels,
        datasets,
      };
      setChartData(data);
    } else {
      setChartData(null);
    }
  },[logs])


  return <>
    <div className="h5 mt-5">
      {title}
    </div>
    { chartData ?
      <div className="row">
        <div className="col-12 col-lg-9 col-xl-10">
          <Line
            ref={chartRef}
            data={chartData || { labels: [], datasets: [] }}
            height="50"
            options={{
              plugins: {
                tooltip: {
                  mode: 'index',
                },
                title: {
                  display: false,
                },
                datalabels: {
                  display: (context: any) => {
                    return context.dataset.data[context.dataIndex] !== 0;
                  },
                  color: "black", 
                  font: {
                    size: 12,
                  },
                },
              },
              scales: {
                x: {
                  ticks: {
                    maxTicksLimit: 10,
                    display: true,
                  },
                  display: true,
                  stacked: true,
                },
                y: {
                  beginAtZero: true,
                  stacked: true,
                  display: true,
                },
              },
              responsive: true,
            }}
          />
        </div>
        <div className="col-12 col-lg-3 col-xl-2">
          <div className="table-responsive mh-350px">
          <table className="table table-striped mt-3">
            <thead>
              <tr>
                <th className="w-75">{label}</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(logs.data).map(([label, data]) => {
                const total = (data as number[]).reduce((sum, value) => sum + value, 0);
                return (
                  <tr key={label}>
                    <td>{label}</td>
                    <td>{total.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    :
      <div className="col-12 mt-2">
        <div className="alert alert-primary">No logs</div>
      </div>
    }
    </>
}

interface Props {
  project: any
}

const APIAccessUsage : React.FC<Props> = ({project}) => {

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [timezoneOffset, setTimezoneOffset] = useState<number>()

  const [logsForStatusCode, setLogsForStatusCode] = useState<ChartData>()
  const [logsForEndpoint, setLogsForEndpoint] = useState<ChartData>()
  const [logsForModel, setLogsForModel] = useState<ChartData>()
  const [logsLoaded, setLogsLoaded] = useState<boolean>(false)
  

  useEffect(() => {
    loadLogs()
  }, [startDate, endDate, timezoneOffset])

  const handleDateSelection = (startDate: string, endDate:string, timezoneOffset:number) => {
    setStartDate(startDate)
    setEndDate(endDate)
    setTimezoneOffset(timezoneOffset)
  }

  async function loadLogs () {
    if( startDate && endDate && timezoneOffset ){
      setLogsLoaded(false)
      let {data: data} = await projectAPI.getAPIRequestLogs(project.id, startDate, endDate, timezoneOffset)
      if( data['data'] ){
        setLogsForStatusCode(data['data']['status_code'])
        setLogsForEndpoint(data['data']['endpoint'])
        setLogsForModel(data['data']['model'])
      }else{
        setLogsForStatusCode(undefined)
        setLogsForEndpoint(undefined)
      }
      setLogsLoaded(true)
    }
  }

  return (
    <>
      <div className="d-flex flex-row align-items-center">
        <BootstrapDateRangePicker onDateSelect={handleDateSelection} minDate={project.created_dt}/>
        {!logsLoaded && (
          <Spinner animation='border' className='ms-4'/>
        )}
      </div>
      <div className="mt-5 border-top">
        {logsLoaded && <RequestChat logs={logsForStatusCode} label="HTTP Status Code" title="HTTP Status Codes"/>}
      </div>
      <div className="mt-5 border-top">
        {logsLoaded && <RequestChat logs={logsForEndpoint} label="Endpoint" title="Endpoints (HTTP 200)"/>}
      </div>
      <div className="mt-5 border-top">
        {logsLoaded && <RequestChat logs={logsForModel} label="Model" title="Models (HTTP 200)"/>}
      </div>
    </>
  )
}

export default APIAccessUsage; 